/** Maximum number of records submitted in a single import request. */
export const MAX_IMPORT_RECORDS = 2000;

export type RiskAssessmentFieldKey =
  | "ReportTitle"
  | "Contractor"
  | "RequestedBy"
  | "SerialNumber"
  | "ConductedBy"
  | "OverallRiskRating"
  | "ReportDate"
  | "Location"
  | "SummaryOfFindings"
  | "Classification";

export type FieldMapping = Partial<Record<RiskAssessmentFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

export const RISK_ASSESSMENT_FIELDS: Array<{
  key: RiskAssessmentFieldKey;
  label: string;
  required?: boolean;
}> = [
  { key: "ReportTitle", label: "Report Title", required: true },
  { key: "Contractor", label: "Contractor", required: true },
  { key: "RequestedBy", label: "Requested By (Agency)", required: true },
  { key: "OverallRiskRating", label: "Overall Risk Rating", required: true },
  { key: "SerialNumber", label: "Serial Number" },
  { key: "ConductedBy", label: "Conducted By" },
  { key: "ReportDate", label: "Report Date" },
  { key: "Location", label: "Location" },
  { key: "SummaryOfFindings", label: "Summary of Findings" },
  { key: "Classification", label: "Classification" },
];

function normalizeStr(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return Math.min(a.length, b.length) / Math.max(a.length, b.length);
  }
  return 0;
}

export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of RISK_ASSESSMENT_FIELDS) {
    const normField = normalizeStr(field.key);
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const score = similarityScore(normField, normalizeStr(header));
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping
) {
  const get = (key: RiskAssessmentFieldKey): string | null => {
    const col = mappings[key];
    if (!col) return null;
    return row[col]?.trim() || null;
  };

  return {
    reportTitle: get("ReportTitle") ?? "",
    contractor: get("Contractor"),
    agency: get("RequestedBy"),
    serialNumber: get("SerialNumber"),
    conductedBy: get("ConductedBy"),
    overallRiskRating: get("OverallRiskRating"),
    reportDate: get("ReportDate"),
    location: get("Location"),
    summaryOfFindings: get("SummaryOfFindings"),
    classification: get("Classification"),
  };
}
